import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, BrainCircuit, Plus, Play, Activity, Workflow, ListTree, Trash2, Plug } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import ModuleDemoVideo from '../components/ModuleDemoVideo';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  AgentSetupGrid,
  CommunityTemplatesPanel,
  TwinCreditsPanel,
  TwinRunModeToggle,
  TwinWorkspacePanel,
} from '../components/ai-studio/AgentAdvancedPanels';

const templateSelectTriggerClass =
  'h-9 w-full sm:w-[min(100%,220px)] bg-zinc-900 border-zinc-700 text-white text-xs shadow-none focus:ring-violet-500/40';
const templateSelectContentClass = 'bg-zinc-900 border-zinc-700 text-zinc-100';
const templateSelectItemClass =
  'text-zinc-100 focus:bg-zinc-800 focus:text-white data-[highlighted]:bg-zinc-800 data-[highlighted]:text-white';

const TABS = ['Chat', 'Agents', 'Monitoring', 'Templates'];

const defaultAgent = {
  id: null,
  name: '',
  description: '',
  agent_type: 'chat',
  role: '',
  system_prompt: '',
  model_settings: {
    provider: 'openai',
    model: 'gpt-4o-mini',
    temperature: 0.7,
    max_tokens: 2048,
    fallback_models: [],
  },
  memory_config: {
    short_term: true,
    long_term: false,
    vector: false,
    session: true,
    tool_memory: false,
    reset_policy: 'per_session',
  },
  tools_config: {
    web_search: false,
    scraping: false,
    file_handling: false,
    email: false,
    crm: false,
    payments: false,
    rest_apis: [],
  },
  connections_config: {
    linked_connection_ids: [],
    default_by_provider: {},
  },
  workflow: {
    nodes: [],
    edges: [],
  },
  execution_mode: 'autonomous',
};

const nodeTypes = [
  { id: 'llm', label: 'LLM Node' },
  { id: 'tool_http', label: 'HTTP Request' },
  { id: 'tool_web_search', label: 'Web Search / Research' },
  { id: 'connection_action', label: 'Connection Action' },
  { id: 'knowledge_retrieve', label: 'Knowledge Retrieve (RAG)' },
  { id: 'condition', label: 'If / Else' },
  { id: 'loop', label: 'Loop / Iterator' },
  { id: 'memory_read', label: 'Memory Read' },
  { id: 'memory_write', label: 'Memory Write' },
  { id: 'webhook', label: 'Webhook Trigger' },
  { id: 'scheduler', label: 'Scheduler' },
  { id: 'output', label: 'Output Formatter' },
];

const AIStudioDashboard = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('Chat');
  const [agents, setAgents] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [editingAgent, setEditingAgent] = useState(defaultAgent);
  const [runs, setRuns] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [agentApiKey, setAgentApiKey] = useState(null);
  const [promptVersions, setPromptVersions] = useState([]);
  const [knowledgeBases, setKnowledgeBases] = useState([]);
  const [chatWidget, setChatWidget] = useState(null);
  const [testInput, setTestInput] = useState('Hello — what can you help me with?');
  const [lastTestResult, setLastTestResult] = useState(null);
  const [agencyTemplates, setAgencyTemplates] = useState([]);
  const [agencyDivisions, setAgencyDivisions] = useState([]);
  const [templateSearch, setTemplateSearch] = useState('');
  const [templateDivision, setTemplateDivision] = useState('');
  const [websiteCategory, setWebsiteCategory] = useState('');
  const [websiteCategories, setWebsiteCategories] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatAgentId, setChatAgentId] = useState(null);
  const [chatAgentName, setChatAgentName] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [planFirst, setPlanFirst] = useState(false);
  const [pendingPlan, setPendingPlan] = useState(false);
  const [runLiveEvents, setRunLiveEvents] = useState([]);
  const [browserFrame, setBrowserFrame] = useState(null);
  const [browserEvents, setBrowserEvents] = useState([]);
  const [studioCredits, setStudioCredits] = useState(null);
  const [chatRunMode, setChatRunMode] = useState('run');
  const [agentStateRefreshKey, setAgentStateRefreshKey] = useState(0);

  const isAgentBuildIntent = (text) => {
    const t = (text || '').trim();
    if (!t) return false;
    return (
      /\b(create|build|make|set up|setup)\b.*\b(agent|assistant|worker|bot)\b/i.test(t)
      || /\b(agent|assistant|worker)\b.*\b(that|to|which)\b/i.test(t)
      || /\bi need (an? )?(agent|assistant)\b/i.test(t)
    );
  };

  const loadChatHistory = async (agentId = null) => {
    try {
      const query = agentId ? `?agent_id=${encodeURIComponent(agentId)}` : '';
      const res = await fetch(`${backendUrl}/api/ai-studio/chat/history${query}`, {
        headers: authHeaders(),
      });
      const data = await res.json();
      if (res.ok && Array.isArray(data.messages)) {
        setChatMessages(data.messages);
      }
      if (res.ok && agentId) {
        setPendingPlan(false);
      } else if (res.ok && data.pending_plan) {
        // Only promote pending from history — never clear it here (avoids races
        // with a just-created plan while an older history request is in flight).
        setPendingPlan(true);
      }
    } catch (err) {
      console.error(err);
    }
  };
  const [templateApplying, setTemplateApplying] = useState(null);
  const [userConnections, setUserConnections] = useState([]);
  const [connectionPlan, setConnectionPlan] = useState(null);
  const [providerActionsMap, setProviderActionsMap] = useState({});

  const workflowSelectClass =
    'bg-zinc-900 border border-zinc-700 rounded-lg px-2 py-1 text-[11px] text-white [color-scheme:dark]';

  const backendUrl = (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL !== '')
    ? process.env.REACT_APP_BACKEND_URL
    : '';

  const authHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };
  };

  const loadAgents = async () => {
    try {
      const res = await fetch(`${backendUrl}/api/ai-studio/apps`, {
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error('Failed to load agents');
      const data = await res.json();
      setAgents(data);
      if (data.length && !selectedId) {
        selectAgent(data[0]);
      }
    } catch (err) {
      console.error(err);
      toast.error('Could not load AI agents');
    }
  };

  const loadStats = async () => {
    try {
      const res = await fetch(`${backendUrl}/api/ai-studio/stats/overview`, {
        headers: authHeaders(),
      });
      if (!res.ok) return;
      const data = await res.json();
      setStats(data);
    } catch (err) {
      console.error(err);
    }
  };

  const loadStudioCredits = async () => {
    try {
      const res = await fetch(`${backendUrl}/api/ai-studio/credits`, { headers: authHeaders() });
      if (!res.ok) return;
      const data = await res.json();
      setStudioCredits(data);
    } catch (err) {
      console.error(err);
    }
  };

  const loadChatRunMode = async (agentId) => {
    if (!agentId) {
      setChatRunMode('run');
      return;
    }
    try {
      const res = await fetch(`${backendUrl}/api/ai-studio/apps/${agentId}/instructions`, {
        headers: authHeaders(),
      });
      if (!res.ok) return;
      const data = await res.json();
      setChatRunMode(data.bindings?.run_mode || 'run');
    } catch (err) {
      console.error(err);
    }
  };

  const loadRuns = async (appId) => {
    if (!appId) return;
    try {
      const res = await fetch(`${backendUrl}/api/ai-studio/apps/${appId}/runs`, {
        headers: authHeaders(),
      });
      if (!res.ok) return;
      const data = await res.json();
      setRuns(data);
    } catch (err) {
      console.error(err);
    }
  };

  const loadAllRuns = async () => {
    try {
      const res = await fetch(`${backendUrl}/api/ai-studio/runs?limit=50`, {
        headers: authHeaders(),
      });
      if (!res.ok) return;
      const data = await res.json();
      setRuns(data.runs || []);
    } catch (err) {
      console.error(err);
    }
  };

  const loadKnowledgeBases = async () => {
    try {
      const res = await fetch(`${backendUrl}/api/ai-studio/knowledge-bases`, { headers: authHeaders() });
      if (res.ok) setKnowledgeBases(await res.json());
    } catch (err) { /* non-fatal */ }
  };

  const createKnowledgeBase = async () => {
    const name = window.prompt('Knowledge base name:');
    if (!name || !name.trim()) return;
    try {
      const res = await fetch(`${backendUrl}/api/ai-studio/knowledge-bases`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ name: name.trim() }),
      });
      if (!res.ok) throw new Error('Failed to create');
      toast.success('Knowledge base created');
      loadKnowledgeBases();
    } catch (err) { toast.error(err.message || 'Failed to create'); }
  };

  const addKnowledgeDoc = async (kbId) => {
    const title = window.prompt('Document title (optional):') || '';
    const content = window.prompt('Paste document text:');
    if (!content || !content.trim()) return;
    try {
      const res = await fetch(`${backendUrl}/api/ai-studio/knowledge-bases/${kbId}/documents`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ title, content }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to add');
      toast.success(`Added ${data.chunk_count} chunk(s)`);
      loadKnowledgeBases();
    } catch (err) { toast.error(err.message || 'Failed to add'); }
  };

  const deleteKnowledgeBase = async (kbId) => {
    if (!window.confirm('Delete this knowledge base and all its documents?')) return;
    try {
      const res = await fetch(`${backendUrl}/api/ai-studio/knowledge-bases/${kbId}`, {
        method: 'DELETE', headers: authHeaders(),
      });
      if (!res.ok) throw new Error('Failed to delete');
      toast.success('Knowledge base deleted');
      loadKnowledgeBases();
    } catch (err) { toast.error(err.message || 'Failed to delete'); }
  };

  useEffect(() => {
    loadAgents();
    loadStats();
    loadKnowledgeBases();
    loadUserConnections();
    loadStudioCredits();
  }, []);

  const loadUserConnections = async () => {
    try {
      const res = await fetch(`${backendUrl}/api/connections`, { headers: authHeaders() });
      if (!res.ok) return;
      const data = await res.json();
      setUserConnections(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    }
  };

  const loadConnectionPlan = async (agentId) => {
    if (!agentId) {
      setConnectionPlan(null);
      return;
    }
    try {
      const res = await fetch(`${backendUrl}/api/ai-studio/apps/${agentId}/connection-plan`, {
        headers: authHeaders(),
      });
      if (!res.ok) return;
      const data = await res.json();
      setConnectionPlan(data.connection_plan || null);
    } catch (err) {
      console.error(err);
    }
  };

  const syncAgentConnections = async (agentId, { link = [], unlink = [] }) => {
    if (!agentId) return null;
    const res = await fetch(`${backendUrl}/api/ai-studio/apps/${agentId}/connections`, {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify({ link, unlink }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Failed to update connections');
    setConnectionPlan(data.connection_plan || null);
    if (data.connections_config) {
      setEditingAgent((a) => (
        a.id === agentId ? { ...a, connections_config: data.connections_config } : a
      ));
    }
    await loadAgents();
    await loadUserConnections();
    return data;
  };

  const linkConnectionToAgent = async (agentId, connectionId, shouldLink = true) => {
    try {
      setLoading(true);
      await syncAgentConnections(agentId, {
        link: shouldLink ? [connectionId] : [],
        unlink: shouldLink ? [] : [connectionId],
      });
      toast.success(shouldLink ? 'Connection linked to agent' : 'Connection unlinked');
    } catch (err) {
      toast.error(err.message || 'Failed to update connections');
    } finally {
      setLoading(false);
    }
  };

  const ensureProviderActions = async (providerId) => {
    if (!providerId || providerActionsMap[providerId]) return;
    try {
      const res = await fetch(`${backendUrl}/api/connections/providers/${providerId}/actions`, {
        headers: authHeaders(),
      });
      if (!res.ok) return;
      const data = await res.json();
      setProviderActionsMap((prev) => ({ ...prev, [providerId]: data.actions || [] }));
    } catch (err) {
      console.error(err);
    }
  };

  const toggleConnectionLink = (connectionId, provider) => {
    const linked = new Set(editingAgent.connections_config?.linked_connection_ids || []);
    const byProvider = { ...(editingAgent.connections_config?.default_by_provider || {}) };
    const wasLinked = linked.has(connectionId);
    if (wasLinked) {
      linked.delete(connectionId);
      if (byProvider[provider] === connectionId) delete byProvider[provider];
    } else {
      linked.add(connectionId);
      byProvider[provider] = connectionId;
    }
    const nextConfig = {
      linked_connection_ids: [...linked],
      default_by_provider: byProvider,
    };
    setEditingAgent((a) => ({
      ...a,
      connections_config: nextConfig,
    }));
    if (editingAgent.id) {
      syncAgentConnections(editingAgent.id, {
        link: wasLinked ? [] : [connectionId],
        unlink: wasLinked ? [connectionId] : [],
      }).catch((err) => toast.error(err.message || 'Failed to sync connection'));
    }
  };

  useEffect(() => {
    if (tab !== 'Workflows') return;
    (editingAgent.workflow?.nodes || []).forEach((node) => {
      if (node.type !== 'connection_action') return;
      const conn = userConnections.find((c) => c.id === node.config?.connection_id);
      if (conn?.provider) ensureProviderActions(conn.provider);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, editingAgent.workflow?.nodes, userConnections]);

  const loadAgencyTemplates = async (
    division = templateDivision,
    search = templateSearch,
    siteCategory = websiteCategory,
  ) => {
    setTemplatesLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Sign in to browse agent templates');
        return;
      }
      const headers = authHeaders();
      // Normalize Select sentinels ("all") so empty = full unfiltered catalog.
      const div = !division || division === 'all' ? '' : division;
      const site = !siteCategory || siteCategory === 'all' ? '' : siteCategory;
      const params = new URLSearchParams();
      if (site) params.set('website_category', site);
      else if (div) params.set('division', div);
      if ((search || '').trim()) params.set('search', (search || '').trim());
      const qs = params.toString();

      // Main list is required; divisions/categories are optional and must not toast on failure.
      const tplRes = await fetch(
        `${backendUrl}/api/ai-studio/templates/agency-agents${qs ? `?${qs}` : ''}`,
        { headers },
      );
      if (!tplRes.ok) {
        const detail = await tplRes.text().catch(() => '');
        throw new Error(detail || `Failed to load templates (${tplRes.status})`);
      }
      const templates = await tplRes.json();
      setAgencyTemplates(Array.isArray(templates) ? templates : []);

      const sideLoads = [];
      if (!agencyDivisions.length) {
        sideLoads.push(
          fetch(`${backendUrl}/api/ai-studio/templates/agency-agents/divisions`, { headers })
            .then(async (res) => {
              if (res.ok) setAgencyDivisions(await res.json());
            })
            .catch(() => {}),
        );
      }
      if (!websiteCategories.length) {
        sideLoads.push(
          fetch(`${backendUrl}/api/ai-studio/templates/website-categories`, { headers })
            .then(async (res) => {
              if (res.ok) setWebsiteCategories(await res.json());
            })
            .catch(() => {}),
        );
      }
      if (sideLoads.length) await Promise.all(sideLoads);
    } catch (err) {
      console.error(err);
      setAgencyTemplates([]);
      const msg = String(err?.message || '');
      if (msg.toLowerCase().includes('sign in')) {
        toast.error(msg);
      } else {
        toast.error('Could not load agent templates');
      }
    } finally {
      setTemplatesLoading(false);
    }
  };

  const clearTemplateFilters = () => {
    setTemplateDivision('');
    setWebsiteCategory('');
    setTemplateSearch('');
    loadAgencyTemplates('', '', '');
  };

  useEffect(() => {
    if (tab === 'Templates') {
      // Pass current filter state explicitly (avoids stale closure / accidental division).
      loadAgencyTemplates(templateDivision, templateSearch, websiteCategory);
    }
    if (tab === 'Monitoring') {
      loadAllRuns();
      loadStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, templateDivision, websiteCategory]);

  useEffect(() => {
    if (tab !== 'Chat') return;
    const savedId = sessionStorage.getItem('ai-studio-chat-agent-id');
    if (savedId && agents.length && !chatAgentId) {
      const match = agents.find((a) => a.id === savedId);
      if (match) {
        setChatAgentId(savedId);
        setChatAgentName(match.name);
        loadChatHistory(savedId);
        return;
      }
    }
    loadChatHistory(chatAgentId);
    loadChatRunMode(chatAgentId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, chatAgentId, agents.length]);

  const applyAgencyTemplate = async (tpl) => {
    setTemplateApplying(tpl.id);
    setTab('Chat');
    setChatAgentId(null);
    setChatAgentName('');
    setPendingPlan(false);
    sessionStorage.removeItem('ai-studio-chat-agent-id');
    setChatLoading(true);
    try {
      const res = await fetch(`${backendUrl}/api/ai-studio/chat/reset`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data.messages)) setChatMessages(data.messages);
      }
      const createRes = await fetch(`${backendUrl}/api/ai-studio/chat`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          message: `Create a ${tpl.name} agent for users`,
          template_id: tpl.id,
          create_new: true,
        }),
      });
      const created = await createRes.json();
      if (!createRes.ok) throw new Error(created.detail || 'Failed to create agent from template');
      if (created.agent_id) {
        setChatAgentId(created.agent_id);
        setChatAgentName(created.agent_name || tpl.name);
        sessionStorage.setItem('ai-studio-chat-agent-id', created.agent_id);
        if (created.connection_plan) setConnectionPlan(created.connection_plan);
        await loadAgents();
        await loadChatHistory(created.agent_id);
      }
      toast.success(`Created “${created.agent_name || tpl.name}” — ready in Chat`);
    } catch (err) {
      toast.error(err.message || 'Could not create agent from template');
    } finally {
      setTemplateApplying(null);
      setChatLoading(false);
    }
  };

  const selectAgent = (agent) => {
    setSelectedId(agent.id);
    setLastTestResult(null);
    setEditingAgent({
      ...defaultAgent,
      ...agent,
      model_settings: { ...defaultAgent.model_settings, ...(agent.model_settings || agent.model_config || {}) },
      memory_config: { ...defaultAgent.memory_config, ...(agent.memory_config || {}) },
      tools_config: { ...defaultAgent.tools_config, ...(agent.tools_config || {}) },
      connections_config: {
        ...defaultAgent.connections_config,
        ...(agent.connections_config || {}),
        linked_connection_ids: agent.connections_config?.linked_connection_ids || [],
        default_by_provider: agent.connections_config?.default_by_provider || {},
      },
      workflow: {
        nodes: agent.workflow?.nodes || [],
        edges: agent.workflow?.edges || [],
      },
    });
    loadRuns(agent.id);
    loadPromptVersions(agent.id);
    loadChatWidget(agent.id);
    loadConnectionPlan(agent.id);
  };

  const startNewAgent = () => {
    setSelectedId(null);
    setLastTestResult(null);
    setEditingAgent(defaultAgent);
    setRuns([]);
    setAgentApiKey(null);
    setPromptVersions([]);
    setChatWidget(null);
  };

  const generateFromDescription = async () => {
    const description = window.prompt('Describe the agent you want, in plain English:');
    if (!description || !description.trim()) return;
    try {
      setLoading(true);
      const res = await fetch(`${backendUrl}/api/ai-studio/apps/generate`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ description }),
      });
      const spec = await res.json();
      if (!res.ok) throw new Error(spec.detail || 'Generation failed');
      setSelectedId(null);
      setAgentApiKey(null);
      setEditingAgent({
        ...defaultAgent,
        name: spec.name,
        description: spec.description,
        agent_type: spec.agent_type,
        role: spec.role,
        system_prompt: spec.system_prompt,
        tools_config: { ...defaultAgent.tools_config, ...(spec.tools_config || {}) },
        workflow: spec.workflow || defaultAgent.workflow,
      });
      toast.success(`Draft agent generated (${spec.generator}). Review & Save.`);
    } catch (err) {
      toast.error(err.message || 'Generation failed');
    } finally {
      setLoading(false);
    }
  };

  const saveAgent = async () => {
    try {
      setLoading(true);
      const body = {
        name: editingAgent.name,
        description: editingAgent.description,
        agent_type: editingAgent.agent_type,
        role: editingAgent.role,
        system_prompt: editingAgent.system_prompt,
        model_settings: editingAgent.model_settings,
        memory_config: editingAgent.memory_config,
        tools_config: editingAgent.tools_config,
        connections_config: editingAgent.connections_config,
        workflow: editingAgent.workflow,
        execution_mode: editingAgent.execution_mode || 'autonomous',
      };

      const isNew = !editingAgent.id;
      const url = isNew
        ? `${backendUrl}/api/ai-studio/apps`
        : `${backendUrl}/api/ai-studio/apps/${editingAgent.id}`;
      const method = isNew ? 'POST' : 'PUT';

      const res = await fetch(url, {
        method,
        headers: authHeaders(),
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed to save agent');
      }

      const saved = await res.json();
      toast.success(isNew ? 'Agent created' : 'Agent updated');
      await loadAgents();
      selectAgent(saved);
      if (chatAgentId === saved.id) {
        setChatAgentName(saved.name);
        await loadConnectionPlan(saved.id);
      }
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Failed to save agent');
    } finally {
      setLoading(false);
    }
  };

  const deleteAgentById = async (agentId, agentName) => {
    if (!agentId) return;
    if (!window.confirm(`Delete "${agentName || 'this agent'}"? This cannot be undone.`)) return;
    try {
      setLoading(true);
      const res = await fetch(`${backendUrl}/api/ai-studio/apps/${agentId}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed to delete agent');
      }
      toast.success('Agent deleted');
      setLastTestResult(null);
      if (chatAgentId === agentId) {
        await startChatSession();
      }
      if (selectedId === agentId || editingAgent.id === agentId) {
        startNewAgent();
      }
      await loadAgents();
      await loadStats();
    } catch (err) {
      toast.error(err.message || 'Failed to delete agent');
    } finally {
      setLoading(false);
    }
  };

  const deleteAgent = async () => {
    if (!editingAgent.id) return;
    await deleteAgentById(editingAgent.id, editingAgent.name);
  };

  const deleteChatAgent = async () => {
    if (!chatAgentId) return;
    await deleteAgentById(chatAgentId, chatAgentName);
  };

  const runTest = async () => {
    if (!editingAgent.id) {
      toast.error('Save the agent before running a test');
      return;
    }
    const input = (testInput || '').trim();
    if (!input) {
      toast.error('Enter a test message first');
      return;
    }
    try {
      setLoading(true);
      const res = await fetch(
        `${backendUrl}/api/ai-studio/apps/${editingAgent.id}/test-run`,
        {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({ input, variables: {} }),
        },
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Test run failed');
      }
      const data = await res.json();
      setLastTestResult(data);
      const mocked = (data.output_text || '').includes('[MOCKED: NO LLM KEY]');
      toast.success(mocked ? 'Test completed (no LLM key — add one in Settings)' : 'Test run completed');
      await loadRuns(editingAgent.id);
      await loadStats();
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Test run failed');
    } finally {
      setLoading(false);
    }
  };

  const generateApiKeyFor = async (agentId) => {
    if (!agentId) return null;
    try {
      const res = await fetch(
        `${backendUrl}/api/ai-studio/apps/${agentId}/api-key`,
        { method: 'POST', headers: authHeaders() },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to generate key');
      if (agentId === editingAgent.id) setAgentApiKey(data);
      return data;
    } catch (err) {
      toast.error(err.message || 'Failed to generate key');
      return null;
    }
  };

  const generateApiKey = async () => {
    if (!editingAgent.id) {
      toast.error('Save the agent before generating an API key');
      return;
    }
    await generateApiKeyFor(editingAgent.id);
  };

  const enableChatWidgetFor = async (agentId) => {
    if (!agentId) return null;
    try {
      const res = await fetch(`${backendUrl}/api/ai-studio/apps/${agentId}/chat-widget`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to enable widget');
      if (agentId === editingAgent.id) {
        setChatWidget(data);
      }
      toast.success('Chat widget enabled');
      return data;
    } catch (err) {
      toast.error(err.message || 'Failed to enable widget');
      return null;
    }
  };

  const enableChatWidget = async () => {
    if (!editingAgent.id) { toast.error('Save the agent first'); return; }
    await enableChatWidgetFor(editingAgent.id);
  };

  const widgetSnippet = (snippet) => {
    if (!snippet) return '';
    const host = backendUrl || window.location.origin;
    return snippet.replace('<YOUR_BACKEND_URL>', host);
  };

  const loadChatWidget = async (appId) => {
    if (!appId) { setChatWidget(null); return; }
    try {
      const res = await fetch(`${backendUrl}/api/ai-studio/apps/${appId}/chat-widget`,
        { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setChatWidget(data.enabled ? data : null);
      }
    } catch (err) { /* non-fatal */ }
  };

  const loadPromptVersions = async (appId) => {
    if (!appId) { setPromptVersions([]); return; }
    try {
      const res = await fetch(`${backendUrl}/api/ai-studio/apps/${appId}/prompt-versions`,
        { headers: authHeaders() });
      if (res.ok) setPromptVersions(await res.json());
    } catch (err) { /* non-fatal */ }
  };

  const savePromptVersion = async () => {
    if (!editingAgent.id) { toast.error('Save the agent first'); return; }
    try {
      const res = await fetch(`${backendUrl}/api/ai-studio/apps/${editingAgent.id}/prompt-versions`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ system_prompt: editingAgent.system_prompt || '' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to save version');
      toast.success(`Saved prompt v${data.version}`);
      loadPromptVersions(editingAgent.id);
    } catch (err) { toast.error(err.message || 'Failed to save version'); }
  };

  const restorePromptVersion = async (version) => {
    if (!version || !editingAgent.id) return;
    try {
      const res = await fetch(
        `${backendUrl}/api/ai-studio/apps/${editingAgent.id}/prompt-versions/${version}/restore`,
        { method: 'POST', headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to restore');
      setEditingAgent((a) => ({ ...a, system_prompt: data.system_prompt }));
      toast.success(`Restored prompt v${version}`);
    } catch (err) { toast.error(err.message || 'Failed to restore'); }
  };

  const addWorkflowNode = () => {
    const id = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}`;
    setEditingAgent((prev) => ({
      ...prev,
      workflow: {
        ...prev.workflow,
        nodes: [
          ...prev.workflow.nodes,
          {
            id,
            type: 'llm',
            label: `LLM Step ${prev.workflow.nodes.length + 1}`,
            config: {
              prompt: '',
            },
          },
        ],
      },
    }));
  };

  const updateWorkflowNode = (id, changes) => {
    setEditingAgent((prev) => ({
      ...prev,
      workflow: {
        ...prev.workflow,
        nodes: prev.workflow.nodes.map((n) =>
          n.id === id ? { ...n, ...changes, config: { ...n.config, ...(changes.config || {}) } } : n,
        ),
      },
    }));
  };

  const removeWorkflowNode = (id) => {
    setEditingAgent((prev) => ({
      ...prev,
      workflow: {
        ...prev.workflow,
        nodes: prev.workflow.nodes.filter((n) => n.id !== id),
        edges: prev.workflow.edges.filter(
          (e) => e.source !== id && e.target !== id,
        ),
      },
    }));
  };

  const startChatSession = async () => {
    setChatAgentId(null);
    setChatAgentName('');
    setConnectionPlan(null);
    setPendingPlan(false);
    sessionStorage.removeItem('ai-studio-chat-agent-id');
    try {
      const res = await fetch(`${backendUrl}/api/ai-studio/chat/reset`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const data = await res.json();
      if (res.ok && Array.isArray(data.messages)) {
        setChatMessages(data.messages);
      }
      setPendingPlan(false);
    } catch (err) {
      toast.error(err.message || 'Could not start new chat');
    }
  };

  const sendStudioChat = async (overrideText) => {
    const text = (overrideText != null ? String(overrideText) : chatInput).trim();
    if (!text || chatLoading) return;
    const isBuildIntent = isAgentBuildIntent(text);
    // Pending plan: keep draft (no agent_id) so Approve/Cancel/revise hit the plan handler.
    const forceNewAgent = pendingPlan || !chatAgentId || isBuildIntent;
    const useStream = Boolean(chatAgentId) && !forceNewAgent && !planFirst && !pendingPlan;
    if (overrideText == null) setChatInput('');
    setChatLoading(true);
    setRunLiveEvents([]);
    setBrowserFrame(null);
    setBrowserEvents([]);
    try {
      if (useStream) {
        setChatMessages((prev) => [...prev, { role: 'user', content: text }]);
        const res = await fetch(`${backendUrl}/api/ai-studio/chat/stream`, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({
            message: text,
            agent_id: chatAgentId,
            create_new: false,
          }),
        });
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.detail || 'Stream failed');
        }
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        const events = [];
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const parts = buffer.split('\n\n');
          buffer = parts.pop() || '';
          for (const part of parts) {
            const line = part.trim().replace(/^data:\s*/, '');
            if (!line) continue;
            try {
              const evt = JSON.parse(line);
              if (evt.type === 'completed') {
                if (evt.agent_id) setChatAgentId(evt.agent_id);
              } else if (evt.type === 'browser.frame' && evt.screenshot) {
                setBrowserFrame({
                  url: evt.url,
                  title: evt.title,
                  screenshot: evt.screenshot,
                  action: evt.action,
                });
                setBrowserEvents((prev) => [...prev, evt]);
                events.push(evt);
                setRunLiveEvents([...events]);
              } else if (evt.type !== 'start') {
                events.push(evt);
                setRunLiveEvents([...events]);
              }
            } catch {
              /* ignore partial SSE chunks */
            }
          }
        }
        await loadChatHistory(chatAgentId);
        await loadStudioCredits();
        setAgentStateRefreshKey((k) => k + 1);
        return;
      }

      const res = await fetch(`${backendUrl}/api/ai-studio/chat`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          message: text,
          agent_id: forceNewAgent ? null : chatAgentId,
          create_new: forceNewAgent && !pendingPlan,
          // Pending plans are revised/approved by the draft handler; plan_first only for new drafts.
          plan_first: planFirst && forceNewAgent && !pendingPlan,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Chat failed');
      setPendingPlan(Boolean(data.pending_plan));
      const activeId = data.agent_id || (data.pending_plan ? null : chatAgentId);
      if (data.created && data.agent_id) {
        setChatAgentId(data.agent_id);
        setChatAgentName(data.agent_name || '');
        sessionStorage.setItem('ai-studio-chat-agent-id', data.agent_id);
        if (data.connection_plan) setConnectionPlan(data.connection_plan);
        await loadAgents();
      }
      if (data.pending_plan && data.connection_plan) {
        setConnectionPlan(data.connection_plan);
      }
      await loadChatHistory(activeId);
      // History can race/miss pending flag; trust the chat response.
      if (data.pending_plan) setPendingPlan(true);
      if (data.created) setPendingPlan(false);
      await loadStudioCredits();
      setAgentStateRefreshKey((k) => k + 1);
    } catch (err) {
      toast.error(err.message || 'Chat failed');
    } finally {
      setChatLoading(false);
      setRunLiveEvents([]);
    }
  };

  const openAgentInChat = (agent) => {
    selectAgent(agent);
    setChatAgentId(agent.id);
    setChatAgentName(agent.name);
    sessionStorage.setItem('ai-studio-chat-agent-id', agent.id);
    loadChatHistory(agent.id);
  };

  const renderConnectionPlanPanel = (agentId) => {
    if (!agentId || !connectionPlan) return null;
    const plan = connectionPlan;
    return (
      <div className="rounded-xl border border-violet-500/20 bg-violet-500/5 p-3 text-xs space-y-2">
        <div className="flex items-center justify-between gap-2">
          <span className="font-semibold text-violet-200 flex items-center gap-1.5">
            <Plug className="w-3 h-3" />
            {plan.readiness_label}
          </span>
          <button
            type="button"
            className="text-[10px] text-violet-300 hover:underline shrink-0"
            onClick={() => navigate('/connections')}
          >
            All connections →
          </button>
        </div>
        {(plan.linked || []).map((item) => (
          <div key={item.connection_id} className="flex items-center justify-between gap-2 text-emerald-300">
            <span className="truncate">✓ {item.label} — linked</span>
            <button
              type="button"
              className="text-[10px] text-zinc-500 hover:text-red-400 shrink-0"
              onClick={() => linkConnectionToAgent(agentId, item.connection_id, false)}
            >
              Unlink
            </button>
          </div>
        ))}
        {(plan.available_to_link || []).map((item) => (
          <div key={item.connection_id} className="flex items-center justify-between gap-2">
            <span className="text-zinc-300 truncate">○ {item.label} — connected, not linked</span>
            <Button
              size="sm"
              variant="outline"
              className="h-6 text-[10px] shrink-0"
              disabled={loading}
              onClick={() => linkConnectionToAgent(agentId, item.connection_id, true)}
            >
              Link
            </Button>
          </div>
        ))}
        {(plan.needs_setup || []).map((item) => (
          <div key={item.provider_id} className="flex items-center justify-between gap-2">
            <span className="text-zinc-400 truncate">+ {item.label} — add for live actions</span>
            <Button
              size="sm"
              variant="outline"
              className="h-6 text-[10px] shrink-0"
              onClick={() => navigate('/connections')}
            >
              Add
            </Button>
          </div>
        ))}
        {(plan.optional_connections || []).length > 0 && (
          <details className="text-zinc-500">
            <summary className="cursor-pointer hover:text-zinc-300 pt-1">Link more integrations</summary>
            <div className="mt-2 space-y-1">
              {(plan.optional_connections || []).map((item) => (
                <div key={item.connection_id} className="flex items-center justify-between gap-2">
                  <span className="truncate">{item.label}</span>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-6 text-[10px] shrink-0"
                    disabled={loading}
                    onClick={() => linkConnectionToAgent(agentId, item.connection_id, true)}
                  >
                    Link
                  </Button>
                </div>
              ))}
            </div>
          </details>
        )}
      </div>
    );
  };

  const renderChatTab = () => (
    <div className="grid grid-cols-1 lg:grid-cols-[240px,1fr] gap-6 max-w-5xl mx-auto">
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Your agents</h2>
          <Button size="sm" variant="outline" className="text-[10px] h-7" onClick={startChatSession}>
            New
          </Button>
        </div>
        <div className="space-y-1 max-h-[420px] overflow-y-auto">
          {agents.map((agent) => (
            <div
              key={agent.id}
              className={`flex items-stretch gap-1 rounded-lg border transition-colors ${
                chatAgentId === agent.id
                  ? 'border-violet-500/60 bg-violet-500/10'
                  : 'border-white/5 hover:border-white/15'
              }`}
            >
              <button
                type="button"
                onClick={() => openAgentInChat(agent)}
                className={`flex-1 min-w-0 text-left px-3 py-2 text-xs ${
                  chatAgentId === agent.id ? 'text-white' : 'text-zinc-400'
                }`}
              >
                <div className="font-medium truncate">{agent.name}</div>
                <div className="text-[10px] text-zinc-500 truncate">{agent.description || agent.agent_type}</div>
              </button>
              <button
                type="button"
                onClick={() => deleteAgentById(agent.id, agent.name)}
                disabled={loading}
                className="shrink-0 px-2 text-zinc-500 hover:text-red-400 hover:bg-red-950/20 rounded-r-lg"
                aria-label={`Delete ${agent.name}`}
                title="Delete agent"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
        <p className="text-[10px] text-zinc-600 leading-relaxed">
          Advanced: Monitoring tab for run history and usage.
        </p>
        <TwinWorkspacePanel backendUrl={backendUrl} authHeaders={authHeaders} />
        <TwinCreditsPanel
          backendUrl={backendUrl}
          authHeaders={authHeaders}
          credits={studioCredits}
          onRefresh={loadStudioCredits}
        />
      </div>

      <div className="glass rounded-2xl border border-white/10 flex flex-col min-h-[520px]">
        <div className="border-b border-white/10 px-4 py-3 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-white truncate">
              {chatAgentId ? chatAgentName || 'Agent' : 'Create with chat'}
            </h2>
            <p className="text-[11px] text-zinc-500">
              {chatAgentId
                ? 'Autonomous mode — tools from Connections run automatically'
                : 'First message creates your agent and links HubSpot, Gmail, etc.'}
            </p>
            {studioCredits && (
              <p className="text-[10px] text-amber-300/90 mt-0.5">
                Credits: {studioCredits.balance} · Build {studioCredits.build_cost} / Run {studioCredits.run_cost} per step
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {chatAgentId ? (
              <TwinRunModeToggle
                agentId={chatAgentId}
                runMode={chatRunMode}
                onChange={setChatRunMode}
                backendUrl={backendUrl}
                authHeaders={authHeaders}
              />
            ) : null}
            {chatAgentId ? (
              <Button
                variant="outline"
                size="sm"
                onClick={deleteChatAgent}
                disabled={loading || chatLoading}
                className="shrink-0 flex items-center gap-1 text-red-400 border-red-900/50 hover:bg-red-950/30"
              >
                <Trash2 className="w-3 h-3" />
                Delete
              </Button>
            ) : null}
          </div>
        </div>

        {chatAgentId ? renderConnectionPlanPanel(chatAgentId) : null}
        {chatAgentId ? (
          <div className="px-4 pb-2 border-b border-white/5 max-h-[440px] overflow-y-auto">
            <p className="text-[10px] text-zinc-500 uppercase tracking-wide mb-2 pt-1">Agent setup</p>
            <AgentSetupGrid
              agentId={chatAgentId}
              backendUrl={backendUrl}
              authHeaders={authHeaders}
              knowledgeBases={knowledgeBases}
              onReloadKnowledgeBases={loadKnowledgeBases}
              userConnections={userConnections}
              onGenerateApiKey={generateApiKeyFor}
              onEnableWidget={enableChatWidgetFor}
              stateRefreshKey={agentStateRefreshKey}
              allAgents={agents}
              browserFrame={browserFrame}
              browserEvents={browserEvents}
              browserExpanded={chatLoading && runLiveEvents.some((e) => e.type === 'browser.frame')}
            />
          </div>
        ) : null}

        <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[360px]">
          {chatMessages.map((msg, i) => (
            <div
              key={`chat-${i}`}
              className={`text-sm whitespace-pre-wrap rounded-xl px-3 py-2 max-w-[92%] ${
                msg.role === 'user'
                  ? 'ml-auto bg-violet-600/30 text-violet-50 border border-violet-500/30'
                  : 'mr-auto bg-zinc-900/80 text-zinc-200 border border-white/10'
              }`}
            >
              {msg.content}
            </div>
          ))}
          {chatLoading && runLiveEvents.length > 0 && (
            <div className="rounded-lg border border-violet-500/20 bg-violet-950/20 p-2 text-[11px] space-y-1">
              <p className="text-violet-300 font-medium">Live run</p>
              {runLiveEvents.slice(-6).map((evt, idx) => (
                <div key={`evt-${idx}`} className="text-zinc-400 truncate">
                  {evt.type === 'phase' && `▸ ${evt.text || ''}`}
                  {evt.type === 'tool.start' && `🔧 ${evt.name}`}
                  {evt.type === 'tool.result' && `${evt.ok ? '✓' : '✗'} ${evt.name}`}
                  {evt.type === 'self_heal' && `↻ retry ${evt.tool}`}
                  {evt.type === 'deliverables' && '📦 deliverables ready'}
                  {evt.type === 'browser.frame' && `🌐 ${evt.action || 'browser'} ${evt.url || ''}`}
                </div>
              ))}
            </div>
          )}
          {chatLoading && runLiveEvents.length === 0 && (
            <div className="text-xs text-zinc-500 animate-pulse">Working…</div>
          )}
        </div>

        <div className="border-t border-white/10 p-4 flex flex-col gap-2">
          {pendingPlan && (
            <div className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2">
              <span className="text-[11px] text-amber-100/90 flex-1 min-w-[140px]">
                Plan ready — approve to build, cancel to discard, or send a message to revise.
              </span>
              <Button
                size="sm"
                className="h-7 text-xs bg-emerald-600 hover:bg-emerald-500"
                disabled={chatLoading}
                onClick={() => sendStudioChat('approve')}
                data-testid="studio-plan-approve"
              >
                Approve
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs border-zinc-600"
                disabled={chatLoading}
                onClick={() => sendStudioChat('cancel')}
                data-testid="studio-plan-cancel"
              >
                Cancel
              </Button>
            </div>
          )}
          {!chatAgentId && !pendingPlan && (
            <label className="flex items-center gap-2 text-[11px] text-zinc-400">
              <input
                type="checkbox"
                checked={planFirst}
                onChange={(e) => setPlanFirst(e.target.checked)}
                className="rounded border-zinc-600"
              />
              Plan first — review before building
            </label>
          )}
          <div className="flex gap-2">
          <Input
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                sendStudioChat();
              }
            }}
            placeholder={
              pendingPlan
                ? 'Revise the plan… or click Approve / Cancel'
                : chatAgentId
                  ? 'Ask your agent… e.g. List my HubSpot contacts'
                  : 'Describe your agent… e.g. Summarize HubSpot contacts for sales'
            }
            className="text-sm flex-1"
            data-testid="studio-chat-input"
          />
          <Button onClick={() => sendStudioChat()} disabled={chatLoading || !chatInput.trim()}>
            Send
          </Button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderAgentsTab = () => (
    <div className="grid grid-cols-1 lg:grid-cols-[260px,1fr] gap-6">
      {/* Left: agent list */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-zinc-300 flex items-center gap-2">
            <BrainCircuit className="w-4 h-4" />
            AI Agents
          </h2>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="outline" onClick={generateFromDescription}
              data-testid="generate-agent" className="text-xs">
              Describe in English
            </Button>
            <Button size="icon" variant="outline" onClick={startNewAgent}>
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
          {agents.length === 0 && (
            <div className="text-xs text-zinc-500">
              No agents yet. Click + to create your first agent.
            </div>
          )}
          {agents.map((agent) => (
            <div
              key={agent.id}
              className={`flex items-stretch gap-1 rounded-xl border text-xs transition-colors ${
                selectedId === agent.id
                  ? 'border-violet-500/70 bg-violet-500/10'
                  : 'border-white/5 hover:border-white/15'
              }`}
            >
              <button
                type="button"
                onClick={() => selectAgent(agent)}
                className="flex-1 min-w-0 text-left px-3 py-2"
              >
                <div className="font-semibold text-sm truncate">{agent.name}</div>
                <div className="text-[11px] text-zinc-400 flex justify-between mt-1">
                  <span>{agent.agent_type || 'chat'}</span>
                  <span>{agent.status || 'draft'}</span>
                </div>
              </button>
              <button
                type="button"
                onClick={() => deleteAgentById(agent.id, agent.name)}
                disabled={loading}
                className="shrink-0 px-2 text-zinc-500 hover:text-red-400 hover:bg-red-950/20 rounded-r-xl"
                aria-label={`Delete ${agent.name}`}
                title="Delete agent"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Right: agent editor */}
      <div className="glass rounded-2xl p-6 space-y-5">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="text-base font-heading font-semibold">Agent configuration</h2>
            <p className="text-xs text-zinc-400">
              Define name, behavior, model and memory. Advanced options are grouped but visible.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap justify-end">
            {editingAgent.id && (
              <Button
                variant="outline"
                size="sm"
                onClick={deleteAgent}
                disabled={loading}
                className="flex items-center gap-1 text-red-400 border-red-900/50 hover:bg-red-950/30"
              >
                <Trash2 className="w-3 h-3" />
                Delete
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={runTest}
              disabled={loading}
              className="flex items-center gap-1"
            >
              <Play className="w-3 h-3" />
              Test
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={generateApiKey}
              data-testid="agent-api-key"
              className="flex items-center gap-1"
            >
              API key
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={enableChatWidget}
              data-testid="agent-chat-widget"
              className="flex items-center gap-1"
            >
              Chat widget
            </Button>
            <Button
              size="sm"
              onClick={saveAgent}
              disabled={loading || !editingAgent.name}
            >
              {loading ? 'Saving…' : 'Save Agent'}
            </Button>
          </div>
        </div>

        {chatWidget && chatWidget.embed_snippet && (
          <div className="mb-4 rounded-lg border border-sky-700/40 bg-sky-950/30 p-3" data-testid="agent-chat-widget-panel">
            <p className="text-xs text-sky-300 mb-1">Embeddable chat widget — paste this before &lt;/body&gt; on any site:</p>
            <code className="block text-[11px] text-sky-100 break-all bg-black/40 rounded px-2 py-1 mb-2">{widgetSnippet(chatWidget.embed_snippet)}</code>
            <p className="text-[11px] text-zinc-400">Public chat token: <span className="text-zinc-300">{chatWidget.chat_token}</span> (safe to expose; disable via Chat widget)</p>
          </div>
        )}

        <div className="rounded-lg border border-white/10 bg-black/30 p-3 space-y-2">
          <label className="block text-xs text-zinc-300">Test message</label>
          <div className="flex gap-2">
            <Input
              value={testInput}
              onChange={(e) => setTestInput(e.target.value)}
              placeholder="Message to send during a test run"
              className="text-xs flex-1"
              data-testid="agent-test-input"
            />
            <Button variant="outline" size="sm" onClick={runTest} disabled={loading || !editingAgent.id}>
              <Play className="w-3 h-3 mr-1" /> Run
            </Button>
          </div>
          {lastTestResult && (
            <div className="mt-2 rounded border border-violet-800/40 bg-violet-950/20 p-2 text-[11px]" data-testid="agent-test-result">
              <div className="text-zinc-400 mb-1">
                Output · {lastTestResult.token_usage?.prompt_tokens ?? 0} prompt + {lastTestResult.token_usage?.completion_tokens ?? 0} completion tokens
              </div>
              <pre className="whitespace-pre-wrap text-zinc-200 max-h-40 overflow-y-auto">{lastTestResult.output_text || '(empty)'}</pre>
            </div>
          )}
        </div>

        {agentApiKey && (
          <div className="mb-4 rounded-lg border border-emerald-700/40 bg-emerald-950/30 p-3" data-testid="agent-api-key-panel">
            <p className="text-xs text-emerald-300 mb-1">API key (shown once — store it now):</p>
            <code className="block text-[11px] text-emerald-100 break-all bg-black/40 rounded px-2 py-1 mb-2">{agentApiKey.api_key}</code>
            <p className="text-[11px] text-zinc-400">Invoke via REST:</p>
            <code className="block text-[11px] text-zinc-300 break-all bg-black/40 rounded px-2 py-1">
              POST {backendUrl}{agentApiKey.invoke_url} — header X-Agent-Key: &lt;key&gt;, body {`{ "input": "..." }`}
            </code>
          </div>
        )}

        {editingAgent.id && (
          <div className="mb-4">
            <p className="text-[10px] text-zinc-500 uppercase tracking-wide mb-2">Agent setup</p>
            <AgentSetupGrid
              agentId={editingAgent.id}
              backendUrl={backendUrl}
              authHeaders={authHeaders}
              knowledgeBases={knowledgeBases}
              onReloadKnowledgeBases={loadKnowledgeBases}
              userConnections={userConnections}
              onGenerateApiKey={generateApiKeyFor}
              onEnableWidget={enableChatWidgetFor}
              stateRefreshKey={agentStateRefreshKey}
            />
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div>
              <label className="block text-xs mb-1">Name</label>
              <Input
                value={editingAgent.name}
                onChange={(e) =>
                  setEditingAgent((a) => ({ ...a, name: e.target.value }))
                }
                placeholder="Customer Support Agent"
              />
            </div>
            <div>
              <label className="block text-xs mb-1">Type</label>
              <select
                className="w-full bg-black border border-white/10 rounded-lg px-3 py-2 text-xs"
                value={editingAgent.agent_type}
                onChange={(e) =>
                  setEditingAgent((a) => ({ ...a, agent_type: e.target.value }))
                }
              >
                <option value="chat">Chat agent</option>
                <option value="task">Task agent</option>
                <option value="research">Research agent</option>
                <option value="automation">Automation agent</option>
              </select>
            </div>
            <div>
              <label className="block text-xs mb-1">Description</label>
              <textarea
                className="w-full bg-black border border-white/10 rounded-lg px-3 py-2 text-xs min-h-[60px]"
                value={editingAgent.description}
                onChange={(e) =>
                  setEditingAgent((a) => ({ ...a, description: e.target.value }))
                }
                placeholder="What should this agent do?"
              />
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <label className="block text-xs mb-1">Role / Purpose</label>
              <Input
                value={editingAgent.role}
                onChange={(e) =>
                  setEditingAgent((a) => ({ ...a, role: e.target.value }))
                }
                placeholder="You are a helpful customer support assistant…"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs">System Prompt</label>
                <div className="flex items-center gap-1">
                  <button type="button" onClick={savePromptVersion}
                    data-testid="save-prompt-version"
                    className="text-[10px] px-2 py-0.5 rounded border border-white/10 text-zinc-300 hover:text-white">
                    Save version
                  </button>
                  {promptVersions.length > 0 && (
                    <select
                      data-testid="restore-prompt-version"
                      onChange={(e) => { restorePromptVersion(e.target.value); e.target.value = ''; }}
                      defaultValue=""
                      className="text-[10px] bg-black border border-white/10 rounded px-1 py-0.5 text-zinc-300">
                      <option value="" disabled>Restore… ({promptVersions.length})</option>
                      {promptVersions.map((v) => (
                        <option key={v.version} value={v.version}>
                          v{v.version}{v.label ? ` — ${v.label}` : ''}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
              <textarea
                className="w-full bg-black border border-white/10 rounded-lg px-3 py-2 text-xs min-h-[80px]"
                value={editingAgent.system_prompt}
                onChange={(e) =>
                  setEditingAgent((a) => ({ ...a, system_prompt: e.target.value }))
                }
                placeholder="Full instructions for how this agent should behave."
              />
            </div>
          </div>
        </div>

        {/* Model & memory quick config */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-white/5 pt-4">
          <div className="space-y-3">
            <h3 className="text-xs font-semibold text-zinc-300">Model</h3>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <label className="block mb-1">Provider</label>
                <select
                  className="w-full bg-black border border-white/10 rounded-lg px-2 py-1.5"
                  value={editingAgent.model_settings?.provider || 'openai'}
                  onChange={(e) =>
                    setEditingAgent((a) => ({
                      ...a,
                      model_settings: { ...(a.model_settings || defaultAgent.model_settings), provider: e.target.value },
                    }))
                  }
                >
                  <option value="openai">OpenAI</option>
                  <option value="google">Google</option>
                  <option value="custom">Custom endpoint</option>
                </select>
              </div>
              <div>
                <label className="block mb-1">Model name</label>
                <Input
                  value={editingAgent.model_settings?.model || ''}
                  onChange={(e) =>
                    setEditingAgent((a) => ({
                      ...a,
                      model_settings: { ...(a.model_settings || defaultAgent.model_settings), model: e.target.value },
                    }))
                  }
                  placeholder="gpt-4o-mini"
                />
              </div>
              <div>
                <label className="block mb-1">Temperature</label>
                <Input
                  type="number"
                  step="0.1"
                  value={editingAgent.model_settings?.temperature ?? 0.7}
                  onChange={(e) =>
                    setEditingAgent((a) => ({
                      ...a,
                      model_settings: {
                        ...(a.model_settings || defaultAgent.model_settings),
                        temperature: parseFloat(e.target.value || '0'),
                      },
                    }))
                  }
                />
              </div>
              <div>
                <label className="block mb-1">Max tokens</label>
                <Input
                  type="number"
                  value={editingAgent.model_settings?.max_tokens ?? 2048}
                  onChange={(e) =>
                    setEditingAgent((a) => ({
                      ...a,
                      model_settings: {
                        ...(a.model_settings || defaultAgent.model_settings),
                        max_tokens: parseInt(e.target.value || '0', 10),
                      },
                    }))
                  }
                />
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <h3 className="text-xs font-semibold text-zinc-300">Memory</h3>
            <div className="grid grid-cols-2 gap-2 text-xs">
              {[
                ['short_term', 'Short-term'],
                ['long_term', 'Long-term'],
                ['vector', 'Vector'],
                ['session', 'Session'],
                ['tool_memory', 'Tool memory'],
              ].map(([key, label]) => (
                <label
                  key={key}
                  className="flex items-center gap-2 rounded-lg border border-white/10 px-2 py-1 cursor-pointer text-[11px]"
                >
                  <input
                    type="checkbox"
                    className="accent-violet-500"
                    checked={editingAgent.memory_config[key]}
                    onChange={(e) =>
                      setEditingAgent((a) => ({
                        ...a,
                        memory_config: {
                          ...a.memory_config,
                          [key]: e.target.checked,
                        },
                      }))
                    }
                  />
                  {label}
                </label>
              ))}
            </div>
            <div>
              <label className="block text-xs mb-1">Reset policy</label>
              <select
                className="w-full bg-black border border-white/10 rounded-lg px-2 py-1.5 text-xs"
                value={editingAgent.memory_config.reset_policy}
                onChange={(e) =>
                  setEditingAgent((a) => ({
                    ...a,
                    memory_config: { ...a.memory_config, reset_policy: e.target.value },
                  }))
                }
              >
                <option value="per_session">Per session</option>
                <option value="manual">Manual reset</option>
                <option value="time_based">Time based</option>
              </select>
            </div>
          </div>
        </div>

        {/* Connections (shared vault) */}
        <div className="border-t border-white/5 pt-4 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-xs font-semibold text-zinc-300">Connections</h3>
            <button
              type="button"
              className="text-[11px] text-violet-300 hover:underline"
              onClick={() => navigate('/connections')}
            >
              Manage →
            </button>
          </div>
          <p className="text-[11px] text-zinc-500">
            Link integrations from the Connections sidebar. Linked apps are used automatically when you chat with this agent.
          </p>
          {editingAgent.id ? renderConnectionPlanPanel(editingAgent.id) : null}
          {userConnections.filter((c) => c.status === 'active').length === 0 ? (
            <p className="text-[11px] text-zinc-500 border border-dashed border-white/10 rounded-lg px-3 py-2">
              No active connections yet. Connect Gmail, Slack, HubSpot, etc. in Connections first.
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
              {userConnections
                .filter((c) => c.status === 'active')
                .map((conn) => {
                  const linked = (editingAgent.connections_config?.linked_connection_ids || []).includes(conn.id);
                  return (
                    <label
                      key={conn.id}
                      className={`flex items-center gap-2 rounded-lg border px-2 py-2 cursor-pointer text-[11px] ${
                        linked ? 'border-violet-500/50 bg-violet-500/10' : 'border-white/10'
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="accent-violet-500"
                        checked={linked}
                        onChange={() => toggleConnectionLink(conn.id, conn.provider)}
                      />
                      <span className="truncate">
                        <span className="text-white">{conn.name}</span>
                        <span className="text-zinc-500"> · {conn.provider}</span>
                      </span>
                    </label>
                  );
                })}
            </div>
          )}
        </div>

        {/* Tools */}
        <div className="border-t border-white/5 pt-4 space-y-3">
          <h3 className="text-xs font-semibold text-zinc-300">Tools</h3>
          <p className="text-[11px] text-zinc-500">
            Optional capability flags for this agent (web search, CRM, email, etc.).
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
            {[
              ['web_search', 'Web search'],
              ['scraping', 'Web scraping'],
              ['file_handling', 'Files / PDFs'],
              ['email', 'Email'],
              ['crm', 'CRM'],
              ['payments', 'Payments'],
            ].map(([key, label]) => (
              <label
                key={key}
                className="flex items-center gap-2 rounded-lg border border-white/10 px-2 py-1 cursor-pointer text-[11px]"
              >
                <input
                  type="checkbox"
                  className="accent-violet-500"
                  checked={!!editingAgent.tools_config?.[key]}
                  onChange={(e) =>
                    setEditingAgent((a) => ({
                      ...a,
                      tools_config: { ...a.tools_config, [key]: e.target.checked },
                    }))
                  }
                />
                {label}
              </label>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderWorkflowTab = () => {
    // NOTE: React hooks like useCallback cannot be used here due to lint rules,
    // so we keep handler functions inline for now.
    // Map logical nodes/edges from agent into React Flow format
    // For now, keep a simple linear list (ReactFlow disabled due to dev overlay)
    const nodes = editingAgent.workflow?.nodes || [];
    const edges = editingAgent.workflow?.edges || [];

    const onNodesChange = () => {};
    const onEdgesChange = () => {};
    const onConnect = () => {};

    const onNodeClick = (evt, node) => {
      const full = (editingAgent.workflow?.nodes || []).find((n) => n.id === node.id);
      if (!full) return;
      // For now, editing happens in the properties list below the canvas
      // (same controls as before)
    };

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Workflow className="w-4 h-4 text-violet-400" />
            <div>
              <h2 className="text-sm font-heading font-semibold">Visual workflow</h2>
              <p className="text-xs text-zinc-400">
                Drag nodes on the canvas and connect them. Execution order follows the graph.
              </p>
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={addWorkflowNode}>
            <Plus className="w-3 h-3 mr-1" /> Add node
          </Button>
        </div>

        {/* Canvas temporarily disabled while we debug ResizeObserver dev overlay */}
        <div className="h-[80px] rounded-2xl border border-dashed border-white/10 bg-black/20 flex items-center justify-center text-[11px] text-zinc-500">
          Canvas preview coming soon. Use the list below to configure nodes.
        </div>

        {/* Simple properties list for the current pipeline (still form-based) */}
        <div className="space-y-3">
          {editingAgent.workflow.nodes.length === 0 ? (
            <div className="text-xs text-zinc-500 border border-dashed border-white/10 rounded-xl p-4 text-center">
              No nodes yet. Click Add node to begin.
            </div>
          ) : (
            <div className="space-y-3">
              {editingAgent.workflow.nodes.map((node, index) => (
                <div
                  key={node.id}
                  className="flex flex-col md:flex-row md:items-center gap-3 border border-white/10 rounded-xl px-3 py-2 bg-black/40"
                >
                  <div className="flex items-center gap-2 min-w-[140px]">
                    <span className="text-[11px] text-zinc-500">Step {index + 1}</span>
                    <select
                      className={workflowSelectClass}
                      value={node.type}
                      onChange={(e) => updateWorkflowNode(node.id, { type: e.target.value })}
                    >
                      {nodeTypes.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex-1 flex flex-col gap-2">
                    <Input
                      className="text-xs"
                      value={node.label}
                      onChange={(e) => updateWorkflowNode(node.id, { label: e.target.value })}
                      placeholder="Describe what this step does in plain English"
                    />
                    {node.type === 'llm' && (
                      <textarea
                        className="w-full bg-black border border-white/10 rounded-lg px-2 py-1 text-[11px] min-h-[60px]"
                        value={node.config?.prompt || ''}
                        onChange={(e) =>
                          updateWorkflowNode(node.id, {
                            config: { ...(node.config || {}), prompt: e.target.value },
                          })
                        }
                        placeholder="Prompt template for this LLM node"
                      />
                    )}
                    {node.type === 'tool_http' && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-[11px]">
                        <Input
                          className="text-xs"
                          value={node.config?.url || ''}
                          onChange={(e) =>
                            updateWorkflowNode(node.id, {
                              config: { ...(node.config || {}), url: e.target.value },
                            })
                          }
                          placeholder="https://api.example.com/resource"
                        />
                        <Input
                          className="text-xs"
                          value={node.config?.method || 'GET'}
                          onChange={(e) =>
                            updateWorkflowNode(node.id, {
                              config: { ...(node.config || {}), method: e.target.value.toUpperCase() },
                            })
                          }
                          placeholder="GET or POST"
                        />
                        <textarea
                          className="w-full bg-black border border-white/10 rounded-lg px-2 py-1 text-[11px] min-h-[40px] md:col-span-2"
                          value={node.config?.body_template || ''}
                          onChange={(e) =>
                            updateWorkflowNode(node.id, {
                              config: { ...(node.config || {}), body_template: e.target.value },
                            })
                          }
                          placeholder="Optional body template, you can use {{input}} placeholder"
                        />
                      </div>
                    )}
                    {node.type === 'tool_web_search' && (
                      <textarea
                        className="w-full bg-black border border-white/10 rounded-lg px-2 py-1 text-[11px] min-h-[60px]"
                        value={node.config?.query || ''}
                        onChange={(e) =>
                          updateWorkflowNode(node.id, {
                            config: { ...(node.config || {}), query: e.target.value },
                          })
                        }
                        placeholder="Query template for research, you can use {{input}} placeholder"
                      />
                    )}
                    {node.type === 'connection_action' && (() => {
                      const activeConns = userConnections.filter((c) => c.status === 'active');
                      const selectedConn = activeConns.find((c) => c.id === node.config?.connection_id)
                        || activeConns.find((c) =>
                          (editingAgent.connections_config?.linked_connection_ids || []).includes(c.id),
                        );
                      const providerId = selectedConn?.provider || node.config?.provider;
                      const actions = providerId ? providerActionsMap[providerId] || [] : [];
                      return (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-[11px]">
                          <select
                            className={workflowSelectClass}
                            value={node.config?.connection_id || ''}
                            onChange={(e) => {
                              const conn = activeConns.find((c) => c.id === e.target.value);
                              updateWorkflowNode(node.id, {
                                config: {
                                  ...(node.config || {}),
                                  connection_id: e.target.value,
                                  provider: conn?.provider || '',
                                  action_id: '',
                                },
                              });
                              if (conn?.provider) ensureProviderActions(conn.provider);
                            }}
                          >
                            <option value="">Select connection…</option>
                            {activeConns.map((c) => (
                              <option key={c.id} value={c.id} className="bg-zinc-900 text-white">
                                {c.name} ({c.provider})
                              </option>
                            ))}
                          </select>
                          <select
                            className={workflowSelectClass}
                            value={node.config?.action_id || ''}
                            onChange={(e) =>
                              updateWorkflowNode(node.id, {
                                config: { ...(node.config || {}), action_id: e.target.value },
                              })
                            }
                            disabled={!providerId}
                          >
                            <option value="">Select action…</option>
                            {actions.map((a) => (
                              <option key={a.id} value={a.id} className="bg-zinc-900 text-white">
                                {a.label || a.id}
                              </option>
                            ))}
                          </select>
                          <textarea
                            className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-2 py-1 text-[11px] text-white min-h-[48px] md:col-span-2"
                            value={node.config?.args_json || ''}
                            onChange={(e) =>
                              updateWorkflowNode(node.id, {
                                config: { ...(node.config || {}), args_json: e.target.value },
                              })
                            }
                            placeholder='Optional action args JSON, e.g. {"message": "{{input}}"}'
                          />
                        </div>
                      );
                    })()}
                    {node.type === 'knowledge_retrieve' && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-[11px]">
                        <select
                          className={workflowSelectClass}
                          value={node.config?.kb_id || ''}
                          onChange={(e) =>
                            updateWorkflowNode(node.id, {
                              config: { ...(node.config || {}), kb_id: e.target.value },
                            })
                          }
                        >
                          <option value="">Select knowledge base…</option>
                          {knowledgeBases.map((k) => (
                            <option key={k.id} value={k.id}>{k.name}</option>
                          ))}
                        </select>
                        <Input
                          type="number"
                          className="text-xs"
                          value={node.config?.top_k || 4}
                          onChange={(e) =>
                            updateWorkflowNode(node.id, {
                              config: { ...(node.config || {}), top_k: parseInt(e.target.value, 10) || 4 },
                            })
                          }
                          placeholder="Top K (default 4)"
                        />
                      </div>
                    )}
                  </div>
                  <Button
                    size="icon"
                    variant="outline"
                    className="shrink-0"
                    onClick={() => removeWorkflowNode(node.id)}
                  >
                    ×
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        <p className="text-[11px] text-zinc-500">
          Note: Branching, parallel execution, loops and sub-workflows will be wired in the execution engine in later phases.
        </p>
      </div>
    );
  };

  const renderKnowledgeTab = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-white">Knowledge Bases (RAG)</h2>
          <p className="text-[11px] text-zinc-500">
            Add documents, then use a "Knowledge Retrieve" node in an agent workflow to ground answers in your content.
          </p>
        </div>
        <Button size="sm" onClick={createKnowledgeBase} data-testid="create-kb">+ New knowledge base</Button>
      </div>
      {knowledgeBases.length === 0 ? (
        <p className="text-xs text-zinc-500">No knowledge bases yet.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {knowledgeBases.map((k) => (
            <div key={k.id} className="border border-white/10 rounded-xl p-4 bg-black/40">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-white">{k.name}</div>
                  <div className="text-[11px] text-zinc-500">{k.chunk_count || 0} chunk(s)</div>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => addKnowledgeDoc(k.id)}>+ Doc</Button>
                  <Button size="sm" variant="outline" onClick={() => deleteKnowledgeBase(k.id)}>Delete</Button>
                </div>
              </div>
              {k.description && <p className="text-[11px] text-zinc-400 mt-2">{k.description}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderMonitoringTab = () => (
    <div className="grid grid-cols-1 lg:grid-cols-[260px,1fr] gap-6">
      <div className="space-y-3">
        <h2 className="text-xs font-semibold text-zinc-300 flex items-center gap-2">
          <Activity className="w-4 h-4" /> Usage & cost
        </h2>
        <div className="glass rounded-2xl p-4 text-xs space-y-2">
          <div className="flex justify-between">
            <span>Total agents</span>
            <span className="font-mono">{stats?.total_apps ?? '–'}</span>
          </div>
          <div className="flex justify-between">
            <span>Total runs</span>
            <span className="font-mono">{stats?.total_runs ?? '–'}</span>
          </div>
          <div className="flex justify-between">
            <span>Estimated cost</span>
            <span className="font-mono">
              ${stats?.total_cost_estimate_usd?.toFixed?.(4) ?? '0.0000'}
            </span>
          </div>
          <p className="text-[11px] text-zinc-500 mt-2">
            Estimated from token usage on completed runs. Add an LLM key in Settings for live responses.
          </p>
        </div>
      </div>

      <div className="space-y-3">
        <h2 className="text-xs font-semibold text-zinc-300 flex items-center gap-2">
          <ListTree className="w-4 h-4" /> Recent runs
        </h2>
        {runs.length === 0 ? (
          <div className="glass rounded-2xl p-4 text-xs text-zinc-500">
            No runs yet. Save an agent and click **Test** to see step-by-step
            traces here.
          </div>
        ) : (
          <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
            {runs.map((run) => (
              <div
                key={run.id || run.run_id}
                className="glass rounded-xl p-3 text-[11px] space-y-1"
              >
                <div className="flex justify-between">
                  <span className="font-mono text-zinc-300 truncate max-w-[180px]">
                    {(run.id || run.run_id || '').slice(0, 8)}…
                  </span>
                  <span className="text-zinc-500">
                    {run.status || 'completed'}
                  </span>
                </div>
                <div className="text-zinc-400">
                  Input: {(run.input_payload?.input || '—').slice(0, 80)}
                </div>
                <div className="text-zinc-300 whitespace-pre-wrap max-h-24 overflow-y-auto">
                  {(run.output_payload?.output_text || run.output_payload?.steps?.slice(-1)?.[0]?.output_preview || '—').slice(0, 300)}
                </div>
                {run.output_payload?.deliverables?.length > 0 && (
                  <div className="text-emerald-400/90">
                    Deliverables: {run.output_payload.deliverables.map((d) => d.label).join(', ')}
                  </div>
                )}
                <div className="text-zinc-500 flex justify-between">
                  <span>Steps: {run.output_payload?.steps?.length ?? '–'}</span>
                  <span>~ ${run.output_payload?.cost_estimate_usd ?? 0}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderTemplatesTab = () => (
    <div className="glass rounded-2xl p-6 space-y-5">
      <div>
        <h2 className="text-sm font-heading font-semibold mb-1">Expert agent templates</h2>
        <p className="text-xs text-zinc-400">
          Full agency catalog (all divisions). Filters are optional — leave on “All” to see every template.
          Each card creates a new agent with a production-grade system prompt you can customise.
        </p>
      </div>

      <div className="flex flex-col sm:flex-row flex-wrap gap-2 text-xs">
        <Input
          value={templateSearch}
          onChange={(e) => setTemplateSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') loadAgencyTemplates(templateDivision, e.target.value, websiteCategory);
          }}
          placeholder="Search templates…"
          className="sm:max-w-xs bg-black/30 border-white/10"
        />
        <Select
          value={templateDivision || 'all'}
          onValueChange={(value) => {
            const next = value === 'all' ? '' : value;
            setTemplateDivision(next);
            if (next) setWebsiteCategory('');
            // Load immediately with the new filter (avoid waiting on stale useEffect state).
            loadAgencyTemplates(next, templateSearch, next ? '' : websiteCategory);
          }}
          disabled={!!websiteCategory}
        >
          <SelectTrigger className={templateSelectTriggerClass} aria-label="Filter by division">
            <SelectValue placeholder="All divisions" />
          </SelectTrigger>
          <SelectContent className={templateSelectContentClass}>
            <SelectItem value="all" className={templateSelectItemClass}>
              All divisions
            </SelectItem>
            {agencyDivisions.map((d) => (
              <SelectItem key={d.id} value={d.id} className={templateSelectItemClass}>
                {d.label} ({d.count})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={websiteCategory || 'all'}
          onValueChange={(value) => {
            const next = value === 'all' ? '' : value;
            setWebsiteCategory(next);
            if (next) setTemplateDivision('');
            loadAgencyTemplates(next ? '' : templateDivision, templateSearch, next);
          }}
          disabled={!!templateDivision}
        >
          <SelectTrigger className={templateSelectTriggerClass} aria-label="Filter by website genre">
            <SelectValue placeholder="All website genres" />
          </SelectTrigger>
          <SelectContent className={templateSelectContentClass}>
            <SelectItem value="all" className={templateSelectItemClass}>
              All website genres
            </SelectItem>
            {websiteCategories.map((c) => (
              <SelectItem key={c.id} value={c.id} className={templateSelectItemClass}>
                {c.icon} {c.label} ({c.agent_count})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          type="button"
          variant="outline"
          className="border-white/10"
          onClick={() => loadAgencyTemplates(templateDivision, templateSearch, websiteCategory)}
        >
          Search
        </Button>
        {(templateDivision || websiteCategory || templateSearch) ? (
          <Button
            type="button"
            variant="ghost"
            className="text-zinc-400 hover:text-white"
            onClick={clearTemplateFilters}
          >
            Show all
          </Button>
        ) : null}
      </div>

      {templatesLoading ? (
        <p className="text-xs text-zinc-500">Loading templates…</p>
      ) : agencyTemplates.length === 0 ? (
        <p className="text-xs text-zinc-500">No templates found. Try “Show all” or a different filter.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 text-xs" data-testid="agency-templates-grid">
          {agencyTemplates.map((tpl) => {
            const divMeta = agencyDivisions.find((d) => d.id === tpl.division);
            return (
              <button
                key={tpl.id}
                type="button"
                disabled={templateApplying === tpl.id}
                onClick={() => applyAgencyTemplate(tpl)}
                className="glass rounded-xl p-4 text-left hover:border-violet-500/40 transition-all disabled:opacity-60"
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="font-semibold flex items-center gap-1.5">
                    {tpl.emoji ? <span>{tpl.emoji}</span> : null}
                    <span>{tpl.name}</span>
                  </div>
                  <span
                    className="shrink-0 rounded-full px-2 py-0.5 text-[10px] border border-white/10 text-zinc-400"
                    style={divMeta?.color ? { borderColor: `${divMeta.color}55`, color: divMeta.color } : undefined}
                  >
                    {divMeta?.label || tpl.division}
                  </span>
                </div>
                <div className="text-zinc-400 mb-2 text-[11px] line-clamp-3">
                  {tpl.description || tpl.vibe || tpl.role}
                </div>
                {tpl.is_primary ? (
                  <span className="inline-block mb-2 text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                    Primary for {websiteCategory || 'site genre'}
                  </span>
                ) : null}
                <span className="text-[11px] text-violet-300">
                  {templateApplying === tpl.id ? 'Creating agent…' : 'Create agent from template →'}
                </span>
              </button>
            );
          })}
        </div>
      )}

      <p className="text-[10px] text-zinc-600 pt-2" data-testid="agency-templates-count">
        {agencyTemplates.length} expert template{agencyTemplates.length === 1 ? '' : 's'} shown
        {templateDivision ? ` in ${agencyDivisions.find((d) => d.id === templateDivision)?.label || templateDivision}` : ''}
        {websiteCategory ? ` for website genre “${websiteCategory}”` : ''}
        {!templateDivision && !websiteCategory && !templateSearch ? ' (full catalog)' : ''}.
      </p>

      <CommunityTemplatesPanel
        backendUrl={backendUrl}
        authHeaders={authHeaders}
        onCloned={(data) => {
          loadAgents();
          if (data.agent?.id) {
            openAgentInChat({ id: data.agent.id, name: data.agent.name, description: data.agent.description });
          }
        }}
      />
    </div>
  );

  return (
    <div className="min-h-screen md:pl-64 p-6 md:p-12">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={() => navigate('/dashboard')}
            className="p-2 rounded-full hover:bg-white/5 text-zinc-400"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="min-w-0">
            <h1 className="text-3xl font-heading font-bold flex items-center gap-2">
              <BrainCircuit className="w-7 h-7 text-violet-400" />
              AI Studio
            </h1>
            <p className="text-zinc-400 text-xs">
              Describe agents in chat — or use Monitoring and Templates for more.
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate('/connections')}
          className="shrink-0 flex items-center gap-1.5 text-xs border-violet-500/40 text-violet-300 hover:bg-violet-500/10"
          data-testid="ai-studio-connections-btn"
        >
          <Plug className="w-3.5 h-3.5" />
          Connections
          {userConnections.filter((c) => c.status === 'active').length > 0 ? (
            <span className="rounded-full bg-violet-500/20 px-1.5 py-0.5 text-[10px] text-violet-200">
              {userConnections.filter((c) => c.status === 'active').length}
            </span>
          ) : null}
        </Button>
      </div>

      <ModuleDemoVideo moduleKey="ai-studio" className="mb-6" />

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-white/10 pb-2 text-xs overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-3 py-1.5 rounded-full border transition-colors whitespace-nowrap ${
              tab === t
                ? 'border-violet-500/70 bg-violet-500/10 text-white'
                : 'border-white/10 text-zinc-400 hover:border-white/40'
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 'Chat' && renderChatTab()}
      {tab === 'Agents' && renderAgentsTab()}
      {tab === 'Monitoring' && renderMonitoringTab()}
      {tab === 'Templates' && renderTemplatesTab()}
    </div>
  );
};

export default AIStudioDashboard;
